import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2, FileUtils } from '@shared/utils';
import {
  MessagesApi,
  RequestStrategyEnum,
  type AddMessageResponse,
  type AddMessageWithAttachmentRequest,
  type ApiResponse,
  type DownloadAttachmentRequest,
  type FileResponse,
  type ReadThreadsRequest,
  type ThreadsResponse,
  type ThreadWithMessages,
  type WithSignal,
} from '@shared/api';

let abortControllers: AbortController[] = [];

const getInitialThreadsEffect = createEffect(({ signal }: WithSignal = {}) => MessagesApi.getThreads(signal));
const getInitialThreadsWithMessages = createEffect(async (data: ApiResponse<ThreadsResponse[]>) => {
  return await Promise.all(
    data.data?.map(async (thread) => {
      const abortController = new AbortController();
      abortControllers.push(abortController);
      const threadMessages = (await MessagesApi.getThreadsMessages(thread.groupId, abortController.signal)).data;
      return { ...thread, messages: threadMessages };
    }) || [],
  );
});
const getThreadsEffect = createEffect(() => MessagesApi.getThreads());
const getThreadsWithMessages = createEffect(async (data: ApiResponse<ThreadsResponse[]>) => {
  return await Promise.all(
    data.data?.map(async (thread) => {
      const abortController = new AbortController();
      abortControllers.push(abortController);
      const threadMessages = (await MessagesApi.getThreadsMessages(thread.groupId, abortController.signal)).data;
      return { ...thread, messages: threadMessages };
    }) || [],
  );
});
const deleteThreadEffect = createEffect(async (groupId: string) => {
  await MessagesApi.deleteThread(groupId);
  return groupId;
});
const addAttachmentForNewThreadEffect = createEffect(
  async (data: { file: FormData | undefined; data: AddMessageResponse | undefined }) =>
    await MessagesApi.addAttachments({ request: data.file!, messageId: data.data!.messageId }),
);
const createNewThreadWithMessageEffect = createEffect(async (request: AddMessageWithAttachmentRequest) => {
  const response = await MessagesApi.addMessage({ subject: request.subject, body: request.body });
  return { file: request.file, data: response.data };
});
const getThreadMessagesEffect = createEffect(async ({ groupId }: { groupId: string | undefined }) => {
  const response = await MessagesApi.getThreadsMessages(groupId!);
  return { groupId, data: response.data };
});
const addAttachmentForNewMessageEffect = createEffect(
  async (data: { file: FormData | undefined; data: AddMessageResponse | undefined; groupId?: string }) => {
    await MessagesApi.addAttachments({ request: data.file!, messageId: data.data!.messageId });
    return { groupId: data.groupId };
  },
);
const addNewMessageToThreadEffect = createEffect(async (request: AddMessageWithAttachmentRequest) => {
  const response = await MessagesApi.addMessage({ groupId: request.groupId, subject: request.subject, body: request.body });
  return { groupId: request.groupId, file: request.file, data: response.data };
});

const readThreadEffect = createEffect(async (request: ReadThreadsRequest) => {
  const response = await MessagesApi.readThread({ groupId: request.groupId });
  return { groupId: request.groupId, data: response.data };
});

const getAttachmentEffect = createEffect(async (request: DownloadAttachmentRequest) => MessagesApi.downloadAttachment(request));
const downloadAttachmentEffect = createEffect(async (params: { data: FileResponse }) => FileUtils.downloadFile(params.data));
const clearEvent = createEvent();

sample({ source: getInitialThreadsEffect.doneData, target: getInitialThreadsWithMessages });
sample({ source: getThreadsEffect.doneData, target: getThreadsWithMessages });
sample({ source: createNewThreadWithMessageEffect.doneData, filter: (data) => !!data.file && !!data.data, target: addAttachmentForNewThreadEffect });
sample({ source: createNewThreadWithMessageEffect.doneData, filter: (data) => !data.file, target: getThreadsEffect });
sample({ source: addAttachmentForNewThreadEffect.doneData, target: getThreadsEffect });
sample({ source: addNewMessageToThreadEffect.doneData, filter: (data) => !!data.file && !!data.data, target: addAttachmentForNewMessageEffect });
sample({ source: addNewMessageToThreadEffect.doneData, filter: (data) => !data.file && !!data.groupId, target: getThreadMessagesEffect });
sample({ source: addAttachmentForNewMessageEffect.doneData, target: getThreadMessagesEffect });
sample({
  source: getAttachmentEffect.doneData,
  filter: (data) => !!data,
  target: downloadAttachmentEffect.prepend((data: FileResponse) => ({ data })),
});

const $dataStore = createStore<ThreadWithMessages[]>([])
  .on(getInitialThreadsWithMessages.done, (current, data) => data.result ?? current)
  .on(getThreadsWithMessages.done, (current, data) => data.result ?? current)
  .on(getThreadMessagesEffect.done, (current, data) =>
    current
      .map((thread) => {
        if (thread.groupId === data.result.groupId) return { ...thread, messages: data.result.data ? data.result.data : thread.messages };
        return thread;
      })
      .sort((a) => (a.groupId === data.result.groupId ? -1 : 1)),
  )
  .on(deleteThreadEffect.done, (current, data) => current.filter((thread) => thread.groupId !== data.result))
  .reset(clearEvent);

const $initialLoadingStore = createStore<boolean>(false)
  .on(getInitialThreadsEffect.pending, (_, data) => data)
  .on(getInitialThreadsEffect.fail, () => false)
  .on(getInitialThreadsWithMessages.pending, (_, data) => data)
  .on(getInitialThreadsWithMessages.fail, () => false)
  .on(getInitialThreadsWithMessages.done, () => false)
  .reset(clearEvent);
const $loading = createStore<boolean>(false)
  .on(getThreadsEffect.pending, (_, data) => data)
  .on(getThreadsEffect.fail, () => false)
  .on(getThreadsWithMessages.pending, (_, data) => data)
  .on(getThreadsWithMessages.fail, () => false)
  .on(getThreadsWithMessages.done, () => false)
  .on(getThreadMessagesEffect.pending, (_, data) => data)
  .on(getThreadMessagesEffect.fail, () => false)
  .on(getThreadMessagesEffect.done, () => false)
  .on(createNewThreadWithMessageEffect.pending, (_, data) => data)
  .on(createNewThreadWithMessageEffect.fail, () => false)
  .on(addAttachmentForNewThreadEffect.pending, (_, data) => data)
  .on(addAttachmentForNewThreadEffect.fail, () => false)
  .on(addNewMessageToThreadEffect.pending, (_, data) => data)
  .on(addNewMessageToThreadEffect.fail, () => false)
  .on(addAttachmentForNewMessageEffect.pending, (_, data) => data)
  .on(addAttachmentForNewMessageEffect.fail, () => false)
  .on(deleteThreadEffect.pending, (_, data) => data)
  .on(deleteThreadEffect.fail, () => false)
  .on(deleteThreadEffect.done, () => false)
  .on(getAttachmentEffect.pending, (_, data) => data)
  .on(getAttachmentEffect.fail, () => false)
  .on(downloadAttachmentEffect.pending, (_, data) => data)
  .on(downloadAttachmentEffect.fail, () => false)
  .on(downloadAttachmentEffect.done, () => false)
  // .on(readThread.pending, (_, data) => data)
  // .on(readThread.fail, () => false)
  // .on(readThread.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getInitialThreadsEffect, () => null)
  // .on(getInitialThreadsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getInitialThreadsEffect.fail, () => 'Something went wrong while fetching Messages. Please try again.')
  .on(getInitialThreadsEffect.done, () => null)
  .on(getInitialThreadsWithMessages, () => null)
  // .on(getInitialThreadsWithMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getInitialThreadsWithMessages.fail, () => 'Something went wrong while fetching Messages. Please try again.')
  .on(getInitialThreadsWithMessages.done, () => null)
  .on(getThreadsEffect, () => null)
  // .on(getThreadsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadsEffect.fail, () => 'Something went wrong while fetching Messages. Please try again.')
  .on(getThreadsEffect.done, () => null)
  .on(getThreadsWithMessages, () => null)
  // .on(getThreadsWithMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadsWithMessages.fail, () => 'Something went wrong while fetching Messages. Please try again.')
  .on(getThreadsWithMessages.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(getThreadMessagesEffect, () => null)
  // .on(getThreadMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadMessagesEffect.fail, () => 'Something went wrong while adding Attachment. Please try again.')
  .on(getThreadMessagesEffect.done, () => null)
  .on(getAttachmentEffect, () => null)
  // .on(getAttachmentEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getAttachmentEffect.fail, () => 'Something went wrong while downloading Attachment. Please try again.')
  .on(getAttachmentEffect.done, () => null)
  .on(createNewThreadWithMessageEffect, () => null)
  // .on(createNewThreadWithMessageEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(createNewThreadWithMessageEffect.fail, () => 'Something went wrong while creating Thread. Please try again.')
  .on(createNewThreadWithMessageEffect.done, () => null)
  .on(addAttachmentForNewThreadEffect, () => null)
  // .on(addAttachmentForNewThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAttachmentForNewThreadEffect.fail, () => 'Something went wrong while adding Attachment. Please try again.')
  .on(addAttachmentForNewThreadEffect.done, () => null)
  .on(addNewMessageToThreadEffect, () => null)
  // .on(addNewMessageToThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addNewMessageToThreadEffect.fail, () => 'Something went wrong while adding New Message. Please try again.')
  .on(addNewMessageToThreadEffect.done, () => null)
  .on(addAttachmentForNewMessageEffect, () => null)
  // .on(addAttachmentForNewMessageEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAttachmentForNewMessageEffect.fail, () => 'Something went wrong while adding Attachment. Please try again.')
  .on(addAttachmentForNewMessageEffect.done, () => null)
  .on(deleteThreadEffect, () => null)
  // .on(deleteThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteThreadEffect.fail, () => 'Something went wrong while deleting Thread. Please try again.')
  .on(deleteThreadEffect.done, () => null)
  .on(downloadAttachmentEffect, () => null)
  .on(downloadAttachmentEffect.fail, () => 'Something went wrong while downloading Attachment. Please try again.')
  .on(downloadAttachmentEffect.done, () => null)
  .on(readThreadEffect, () => null)
  .on(readThreadEffect.fail, () => 'Something went wrong while reading Thread. Please try again.')
  .on(readThreadEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getInitialThreadsApi = ApiUtilsV2.wrapperApi(getInitialThreadsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => {
  getInitialThreadsApi.cancel();
  abortControllers.forEach((carAbortController) => carAbortController.abort());
  abortControllers = [];
});

export const $messageCentreStore = {
  events: {
    getThreads: getInitialThreadsApi.trigger,
    createNewThreadWithMessage: createNewThreadWithMessageEffect,
    addNewMessageToThread: addNewMessageToThreadEffect,
    deleteThread: deleteThreadEffect,
    downloadAttachment: getAttachmentEffect,
    readThread: readThreadEffect,
    clear: clearEvent,
  },
  store: combine({
    firstLoading: $initialLoadingStore,
    loading: $loading,
    data: $dataStore,
    error: $errorStore,
  }),
};
